import React, { FormEvent, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface MessageInputProps {
  messageInput: string;
  setMessageInput: (value: string) => void;
  onSendMessage: (e: FormEvent) => void;
  isDisabled: boolean;
}

const commonEmojis = ['😊', '👍', '🙌', '😂', '🎉', '❤️', '👋', '🔥', '👀', '🤔', '😍', '🙏', '👏', '🤣', '😎', '✅', '🎊'];

/**
 * Message input area with emoji picker
 */
export const MessageInput: React.FC<MessageInputProps> = ({
  messageInput,
  setMessageInput,
  onSendMessage,
  isDisabled
}) => {
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

  const insertEmoji = (emoji: string) => {
    setMessageInput(messageInput + emoji);
    setEmojiPickerOpen(false);
  };

  return (
    <div className="p-3 border-t border-gray-700">
      <form className="flex items-center space-x-2" onSubmit={onSendMessage}>
        {/* Emoji picker */}
        <div className="relative">
          <Popover open={emojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground transition-colors p-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isDisabled}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                     strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                  <line x1="9" y1="9" x2="9.01" y2="9"/>
                  <line x1="15" y1="9" x2="15.01" y2="9"/>
                </svg>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2 bg-[#1E293B] border border-slate-700">
              <div className="grid grid-cols-5 gap-2">
                {commonEmojis.map((emoji, index) => (
                  <button
                    key={index}
                    type="button"
                    className="text-xl hover:bg-slate-800 p-2 rounded transition-colors"
                    onClick={() => insertEmoji(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Text input */}
        <div className="flex-1 bg-background rounded-lg overflow-hidden flex items-center">
          <Input
            type="text"
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            placeholder="Type a message..."
            className="w-full bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            disabled={isDisabled}
          />
        </div>

        {/* Send button */}
        <Button
          type="submit"
          className="bg-[hsl(189,85%,38%)] hover:bg-[hsl(189,85%,30%)] text-white px-3 h-10 rounded-lg transition-colors shadow-md"
          disabled={isDisabled}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </Button>
      </form>
    </div>
  );
};
